import { useEffect } from 'react';
import { useMutation, useQuery, skipToken } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTime } from 'luxon';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';

import {
    GenerateCertificateDocument,
    ICertificateWithScoringFragment,
    CertificateDocument,
    CreateExternalReceivedCertificateDocument,
} from 'graphql/types';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { CertificateInfo } from 'training/components/CertificateInfo';
import { Divider } from 'common/components/Divider';
import {
    CertificateSelectField,
    DatePickerField,
    FileField,
    TextField,
} from 'common/forms/Field';
import { getGenerateCertificateFormValidator } from 'common/forms/validators';
import {
    EXTERNAL_CERTIFICATE_FILE_MAX_SIZE,
    EXTERNAL_CERTIFICATE_FILE_TYPES,
} from 'common/constants/files';

type TCertificateGenerateDialogFormValues = {
    certificate: ICertificateWithScoringFragment | null;
    title: string;
    issueDate: DateTime | null;
    expiryDate?: DateTime | null;
    fileId?: string;
};

interface IProps {
    certificateId?: string;
    confirmText?: string;
    isExternalCertificate?: boolean;
    title?: string;
    text?: string;
    userIds: string[];
    open: boolean;
    onClose(): void;
    onGenerateCertificate?(): void;
}

export const CertificateGenerateDialog = ({
    certificateId,
    confirmText,
    isExternalCertificate,
    title,
    text,
    userIds,
    open,
    onClose,
    onGenerateCertificate,
}: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();

    const { trainingId } = useParams() as { trainingId?: string };

    const {
        data: certificateData,
        loading: loadingCertificate,
        error: certificateError,
    } = useQuery(
        CertificateDocument,
        certificateId && !isExternalCertificate
            ? { variables: { id: certificateId } }
            : skipToken
    );

    useEffect(() => {
        if (certificateError) {
            showApolloError(certificateError);
        }
    }, [certificateError, showApolloError]);

    const [generateCertificate, { loading: loadingGenerateCertificate }] =
        useMutation(GenerateCertificateDocument);

    const [
        createExternalCertificate,
        { loading: loadingCreateExternalCertificate },
    ] = useMutation(CreateExternalReceivedCertificateDocument);

    const certificate = certificateData?.certificate;

    const validationSchema = getGenerateCertificateFormValidator(
        certificate,
        isExternalCertificate
    );

    const { control, handleSubmit, formState, reset } =
        useForm<TCertificateGenerateDialogFormValues>({
            resolver: zodResolver(validationSchema),
            defaultValues: {
                certificate: null,
                issueDate: DateTime.now(),
                title: !!certificate ? certificate.name : '',
            },
        });

    useEffect(() => {
        if (open) {
            reset({
                certificate: null,
                issueDate: DateTime.now(),
                title: !!certificate ? certificate.name : '',
            });
        }
    }, [open, certificate, reset, isExternalCertificate]);

    if (!open) return null;

    const certificateCount = userIds.length;

    const onSubmit = async (values: TCertificateGenerateDialogFormValues) => {
        const {
            certificate: selectedCertificate,
            title,
            issueDate,
            expiryDate,
            fileId,
        } = values;

        const resolvedCertificateId =
            certificate?.id || selectedCertificate?.id || certificateId;
        if (!resolvedCertificateId) return;

        try {
            if (isExternalCertificate) {
                if (!fileId) return;

                await createExternalCertificate({
                    variables: {
                        certificateId: resolvedCertificateId,
                        receivedCertificate: {
                            title,
                            expiryDate: expiryDate?.toISO(),
                            issueDate: issueDate?.toISO(),
                            fileId,
                        },
                        userId: userIds[0],
                    },
                });
            } else {
                await generateCertificate({
                    variables: {
                        id: resolvedCertificateId,
                        title,
                        userIds,
                        trainingId,
                        issueDate: issueDate?.toISO(),
                    },
                });
            }

            displaySnackbar(
                translate('certificateGenerateDialog.success', {
                    count: certificateCount,
                }),
                {
                    variant: 'success',
                }
            );

            onGenerateCertificate?.();
            onClose?.();
        } catch (error) {
            showApolloError(error);
        }
    };

    const issueDateField = (
        <DatePickerField
            control={control}
            label={translate('certificateGenerateDialog.issueDate')}
            name="issueDate"
        />
    );

    return (
        <ConfirmDialog
            fullWidth
            confirmText={
                confirmText ||
                translate('certificateGenerateDialog.confirmText', {
                    count: certificateCount,
                })
            }
            loading={
                formState.isSubmitting ||
                loadingGenerateCertificate ||
                loadingCreateExternalCertificate ||
                loadingCertificate
            }
            maxWidth="sm"
            open={open}
            title={
                title ||
                translate('certificateGenerateDialog.title', {
                    count: certificateCount,
                })
            }
            onCancel={onClose}
            onClose={onClose}
            onConfirm={handleSubmit(onSubmit)}
        >
            <Typography sx={{ mb: 1 }}>
                {text ||
                    translate('certificateGenerateDialog.text', {
                        count: certificateCount,
                    })}
            </Typography>

            <form>
                {!isExternalCertificate && (
                    <>
                        {!certificateId && (
                            <CertificateSelectField
                                control={control}
                                name="certificate"
                            />
                        )}

                        {!!certificate && (
                            <>
                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ my: 2 }}>
                                    <CertificateInfo
                                        certificate={certificate}
                                    />
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {issueDateField}
                            </>
                        )}

                        {!loadingCertificate && !trainingId && (
                            <TextField
                                control={control}
                                label={translate(
                                    !!certificate
                                        ? 'certificateDescription'
                                        : 'title'
                                )}
                                margin="dense"
                                name="title"
                            />
                        )}
                    </>
                )}

                {isExternalCertificate && (
                    <>
                        <Divider sx={{ mt: 2, mb: 4 }} />

                        <FileField
                            acceptedFileTypes={EXTERNAL_CERTIFICATE_FILE_TYPES}
                            control={control}
                            id="certificateFile"
                            label={translate(
                                'certificateGenerateDialog.certificateFile'
                            )}
                            maxUploadSize={EXTERNAL_CERTIFICATE_FILE_MAX_SIZE}
                            name="fileId"
                        />

                        <Grid
                            container
                            spacing={{ xs: 0, sm: 2 }}
                            sx={{ mt: 2 }}
                        >
                            <Grid size={{ xs: 12, sm: 6 }}>
                                {issueDateField}
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DatePickerField
                                    control={control}
                                    label={translate(
                                        'certificateGenerateDialog.expiryDate'
                                    )}
                                    name="expiryDate"
                                />
                            </Grid>
                        </Grid>
                    </>
                )}
            </form>
        </ConfirmDialog>
    );
};
